import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { FloorPlanDemo } from "@/components/FloorPlanDemo";
import {
  ArrowLeft,
  ArrowRight,
  Calculator,
  UtensilsCrossed,
  Users,
  MonitorSpeaker,
  Wifi,
  Grid3X3,
  Music,
  Timer,
  Volume2,
  ClipboardList,
  FileSearch,
  AlertTriangle,
  ArrowRightLeft,
  Sparkles,
  MousePointerClick,
  Layers,
  Save,
  HelpCircle,
  BookOpen,
} from "lucide-react";

// -----------------------------
// Data — every tool documented
// -----------------------------
const TOOLS = [
  // ------------- Planning -------------
  {
    id: "room-capacity",
    suite: "planning",
    icon: Calculator,
    name: "Room Capacity",
    path: "/planning/room-capacity",
    tagline:
      "How many bums can you fit in a room, safely and legally, in every layout?",
    metaTitle: "Room Capacity Calculator for Events — evntkit",
    metaDescription:
      "Calculate maximum event attendees for Theatre, Classroom, Banquet, Cabaret and Banquet+Dancefloor layouts. Metric or imperial, with UK safety code defaults.",
    features: [
      "Enter room width × length in metres (or feet) and get instant capacities for Theatre, Classroom, Banquet, Cabaret, and Banquet+Dancefloor.",
      "Advanced parameters: aisle width, obstruction area, and safety code (default UK 0.65 m²/person).",
      "One-tap ‘Send to Floor Plan’ per layout — auto-generates chairs, tables, and stage in the 2D editor.",
      "Inputs persist across navigation so you never lose your setup.",
    ],
  },
  {
    id: "catering",
    suite: "planning",
    icon: UtensilsCrossed,
    name: "Catering Estimator",
    path: "/planning/catering",
    tagline:
      "Waste-Zero food & beverage quantities, with a bonus Seated-Buffet chair planner.",
    metaTitle: "Event Catering Estimator — Food & Drink Quantities · evntkit",
    metaDescription:
      "Calculate exact food and drink quantities for cocktails, gala, buffet and wedding events. Waste-Zero methodology plus a Seated-Buffet chair planner.",
    features: [
      "Calculates food and drink quantities based on guest count, event duration, and event type (cocktail, gala, buffet, wedding).",
      "Seated Buffet – Chair Planner: enter guests + comfort fill rate, get exact chairs and tables needed.",
      "Send to Floor Plan: auto-places calculated rounds in the 2D editor.",
      "Results save so you can adjust one variable and see impact instantly.",
    ],
  },
  {
    id: "staffing",
    suite: "planning",
    icon: Users,
    name: "Staffing Planner",
    path: "/planning/staffing",
    tagline: "How many staff, how many shifts — for front-of-house and catering.",
    metaTitle: "Event Staffing Planner — Servers & FoH · evntkit",
    metaDescription:
      "Work out how many servers and front-of-house staff you need for gala dinners, buffets and mixed events, based on industry-standard ratios.",
    features: [
      "Separate calculators for Front-of-House, Buffet Catering, and Gala Dinner Catering.",
      "Uses proven industry ratios (e.g. 1 server per 20 gala guests, 1 per 50 buffet guests).",
      "Accounts for event duration and break coverage.",
    ],
  },
  {
    id: "av-specs",
    suite: "planning",
    icon: MonitorSpeaker,
    name: "AV Tech Specs",
    path: "/planning/av-specs",
    tagline:
      "Screen sizing, sightlines, off-axis check, and projector brightness in one tool.",
    metaTitle: "AV Tech Specs — Screen Size, Sightlines & Lumens · evntkit",
    metaDescription:
      "Size screens, check sightlines and off-axis seats (THX 30°), calculate projector lumens or pick an LED pixel pitch — all in metric.",
    features: [
      "Choose Projector or LED Wall mode — inputs adapt.",
      "Sightline & Legibility check: front-row too close? back-row too far? Off-axis seats warned automatically (30° THX limit).",
      "Screen sizing based on room depth, content type (text-dense vs cinematic), and aspect ratio (16:9, 16:10, 4:3, 21:9).",
      "Projector Brightness (ANSI lumens) recommendation based on screen area + ambient light.",
      "LED pixel-pitch min viewing distance table (1.9 – 6 mm).",
    ],
  },
  {
    id: "wifi",
    suite: "planning",
    icon: Wifi,
    name: "WiFi Predictor",
    path: "/planning/wifi",
    tagline: "Bandwidth and AP count so the CEO's Zoom call doesn't drop.",
    metaTitle: "Event WiFi Bandwidth Predictor — evntkit",
    metaDescription:
      "Estimate bandwidth and access-point count for your event based on attendees and how they'll use the network. Built-in 30% headroom.",
    features: [
      "Enter attendee count and usage profile (email/browsing, streaming, video-call heavy).",
      "Returns peak bandwidth (Mbps) with a 30 % headroom buffer built in.",
      "Access-point count suggestion for typical conference density.",
    ],
  },
  {
    id: "floor-plan",
    suite: "planning",
    icon: Grid3X3,
    name: "Floor Plan Tool",
    path: "/planning/floor-plan",
    tagline: "2D drag-and-drop editor — round tables, stage, dancefloor, chairs.",
    metaTitle: "Event Floor Plan Tool — 2D Layout Editor · evntkit",
    metaDescription:
      "Drag-and-drop 2D floor plan editor for weddings, galas and conferences. Round & rectangle tables, stage, dancefloor, chairs — save, export PNG.",
    demo: FloorPlanDemo,
    features: [
      "Elements: round table (1.83 m), rectangle table (1.83 × 0.76 m), stage, dancefloor, chair, podium, bar/buffet, entrance.",
      "Configurable sizes for stage, dancefloor, and chairs-per-round-table before adding.",
      "Group / Array placement: place N × M identical elements at once, then move/rotate/delete as a group.",
      "Canvas: zoom 50 – 200 %, snap-to-grid, rotate CW/CCW in 15° increments, touch-friendly on tablets.",
      "Save unlimited named plans to your account; download PNG snapshot for emails.",
      "Auto-generated from Room Capacity and Catering with a persistent back-strip.",
    ],
  },
  {
    id: "dancefloor",
    suite: "planning",
    icon: Music,
    name: "Dancefloor Calculator",
    path: "/planning/dancefloor-calculator",
    tagline: "The right size dancefloor for the vibe — not a metre wasted.",
    metaTitle: "Dancefloor Size Calculator — evntkit",
    metaDescription:
      "Get the right dancefloor size based on dance style (packed to performance) and percentage of guests expected to dance at peak.",
    features: [
      "Pick a dance style: packed club (0.4 m²/pax), social (0.6), mixed (0.75), formal (1.0), performance (1.5).",
      "Enter total guests and expected % dancing at peak.",
      "Recommends square, rectangular, and round dancefloor dimensions.",
    ],
  },
  // ------------- Execution -------------
  {
    id: "sync-timer",
    suite: "execution",
    icon: Timer,
    name: "Sync Timer",
    path: "/execution/timer",
    tagline:
      "One master countdown, synced across your entire crew via a shareable 6-char code.",
    metaTitle: "Sync Timer — Shareable Countdown for Event Crew · evntkit",
    metaDescription:
      "One master countdown timer synced across your whole crew via a shareable code. Pause, resume, add time — every device stays in step.",
    features: [
      "Set duration with H/M/S inputs or one-tap presets (1 hr, 45, 30, 15, 5, 1 min).",
      "Generate a shareable code — any device that joins with the code sees the same countdown in real time.",
      "Pause, resume, and add/subtract time on the fly — every joined device updates.",
      "Fullscreen mode for green-room screens or backstage monitors.",
    ],
  },
  {
    id: "decibel",
    suite: "execution",
    icon: Volume2,
    name: "Decibel Meter",
    path: "/execution/decibel",
    tagline: "Live venue noise monitor with a 90 dB visual alert.",
    metaTitle: "Decibel Meter — Venue Noise Monitor · evntkit",
    metaDescription:
      "Monitor venue noise levels live from any device with a browser microphone. 90 dB visual alerts to protect guests and enforce venue clauses.",
    features: [
      "Uses the browser microphone via WebAudio (no install).",
      "Colour bar goes green → amber → red as levels approach the threshold.",
      "Handy for enforcing venue noise-limit clauses and protecting guest hearing.",
    ],
    note: "Requires microphone permission. Currently in beta — accuracy varies by device.",
  },
  {
    id: "tally",
    suite: "execution",
    icon: ClipboardList,
    name: "Dinner Service Tally",
    path: "/execution/tally",
    tagline: "Tick off starter/main/dessert per table — the whole team stays in sync.",
    metaTitle: "Dinner Service Tally — Live Course Tracker · evntkit",
    metaDescription:
      "Track starter, main and dessert service per table across your whole serving team, in real time. Public share links, no login needed.",
    features: [
      "Create a session, get a 6-char public share code.",
      "Share the URL with servers — no login required for shared links.",
      "Colour-coded table statuses: sage = served, cream = pending. Auto-syncs every 3 seconds.",
      "Perfect for banquets, weddings, and gala dinners with multiple tables and servers.",
    ],
  },
  // ------------- Solver -------------
  {
    id: "contract",
    suite: "solver",
    icon: FileSearch,
    name: "Contract Scanner",
    path: "/solver/contract",
    tagline: "AI reads supplier contracts and flags hidden costs & red flags.",
    metaTitle: "AI Contract Scanner for Events — evntkit",
    metaDescription:
      "Paste any venue, caterer or supplier contract and let AI flag hidden costs, auto-renewal clauses, cancellation terms and red-flag language.",
    features: [
      "Paste any contract or T&Cs — venue, caterer, AV supplier, transport.",
      "AI returns a formatted report: hidden costs, auto-renewal clauses, cancellation windows, unusual terms, and a plain-English summary.",
      "Rendered as Markdown so you can copy-paste sections into an email.",
    ],
    note: "AI-powered — always cross-check flagged items with the actual contract text.",
  },
  {
    id: "emergency",
    suite: "solver",
    icon: AlertTriangle,
    name: "Emergency Script Generator",
    path: "/solver/emergency",
    tagline: "AI-drafted calm evacuation & incident scripts — in seconds.",
    metaTitle: "AI Emergency Script Generator for Events — evntkit",
    metaDescription:
      "Generate calm, professional emergency and evacuation announcement scripts for events. Editable, with one-tap text-to-speech playback.",
    features: [
      "Pick a scenario (fire, medical, weather, security) and event context.",
      "AI generates a professional, calm-tone announcement script.",
      "Edit the script inline before use.",
      "One-tap: convert to speech via OpenAI TTS and play the audio.",
    ],
  },
  {
    id: "converter",
    suite: "solver",
    icon: ArrowRightLeft,
    name: "Currency & Unit Converter",
    path: "/solver/converter",
    tagline: "For events that cross borders and unit systems.",
    metaTitle: "Currency & Unit Converter for Event Planners — evntkit",
    metaDescription:
      "Convert currency, length, area, weight, volume and temperature for cross-border event planning. Metric ↔ imperial in one tap.",
    features: [
      "Currency conversion for major event-industry currencies (GBP, USD, EUR, AUD, CAD, etc.).",
      "Unit conversion: length (m/ft), area (m²/ft²), weight (kg/lb), volume (L/gal), temperature (°C/°F).",
      "Handy for briefing overseas venues in their local units.",
    ],
  },
];

const SUITES = [
  {
    id: "planning",
    label: "Planning Suite",
    color: "text-cyan-400",
    dot: "bg-cyan-400",
    icon: Calculator,
    blurb: "Calculators & estimators before the event.",
  },
  {
    id: "execution",
    label: "Execution Suite",
    color: "text-yellow-400",
    dot: "bg-yellow-400",
    icon: Timer,
    blurb: "On-site tools during the event.",
  },
  {
    id: "solver",
    label: "Solver Suite",
    color: "text-purple-400",
    dot: "bg-purple-400",
    icon: Sparkles,
    blurb: "AI tools for problem moments.",
  },
];

// -----------------------------
// Dynamic SEO — updates <title>, meta description, OG, and Twitter tags
// as the user navigates between anchors (#floor-plan, #room-capacity, …).
// -----------------------------
const DEFAULT_META = {
  title: "How to use evntkit — Help Centre",
  description:
    "Every evntkit tool explained: room capacity, catering estimator, staffing planner, AV specs, WiFi predictor, floor plan editor, dancefloor sizing, sync timer, decibel meter, dinner tally, AI contract scanner, emergency script generator, unit converter.",
};
const CANONICAL_ORIGIN = "https://eventstack-2.emergent.host";

const setOrCreateMeta = (attr, key, value) => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setCanonical = (url) => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
};

const applyHelpSeo = (tool) => {
  const title = tool?.metaTitle || DEFAULT_META.title;
  const description = tool?.metaDescription || DEFAULT_META.description;
  const url = tool
    ? `${CANONICAL_ORIGIN}/help#${tool.id}`
    : `${CANONICAL_ORIGIN}/help`;

  document.title = title;
  setOrCreateMeta("name", "description", description);
  setOrCreateMeta("property", "og:title", title);
  setOrCreateMeta("property", "og:description", description);
  setOrCreateMeta("property", "og:url", url);
  setOrCreateMeta("property", "og:type", "article");
  setOrCreateMeta("name", "twitter:title", title);
  setOrCreateMeta("name", "twitter:description", description);
  setCanonical(url);
};
const ToolCard = ({ tool, isAuthenticated }) => {
  const Icon = tool.icon;
  const suite = SUITES.find((s) => s.id === tool.suite);
  return (
    <Card
      id={tool.id}
      className="tactical-card scroll-mt-20"
      data-testid={`help-tool-${tool.id}`}
    >
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div
              className={`p-2.5 rounded-sm bg-secondary ${suite.color}`}
            >
              <Icon className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${suite.dot}`} />
                <span
                  className={`text-[10px] uppercase tracking-widest ${suite.color}`}
                >
                  {suite.label}
                </span>
              </div>
              <CardTitle className="text-xl uppercase tracking-wide mt-1">
                {tool.name}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                {tool.tagline}
              </p>
            </div>
          </div>
          {isAuthenticated && (
            <Link to={tool.path}>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                data-testid={`help-open-${tool.id}`}
              >
                Open tool
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground leading-relaxed space-y-4">
        {tool.demo && (
          <tool.demo />
        )}
        <ul className="space-y-2 list-disc pl-5">
          {tool.features.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
        {tool.note && (
          <p className="mt-3 text-xs italic border-l-2 border-border pl-3">
            {tool.note}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const SuiteNav = ({ suite, tools }) => (
  <div key={suite.id} className="space-y-2">
    <div className="flex items-center gap-2">
      <span className={`h-1.5 w-1.5 rounded-full ${suite.dot}`} />
      <span className={`text-[11px] uppercase tracking-widest ${suite.color}`}>
        {suite.label}
      </span>
    </div>
    <ul className="space-y-1 pl-4 border-l border-border/60">
      {tools.map((t) => (
        <li key={t.id}>
          <a
            href={`#${t.id}`}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors block py-0.5"
            data-testid={`help-nav-${t.id}`}
          >
            {t.name}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

// -----------------------------
// Main page
// -----------------------------
export const Help = () => {
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const logoSrc =
    theme === "light" ? "/logos/Evntkit_Logo.png" : "/logos/Evntkit_Logo_White.png";

  // Dynamic SEO — react to hash changes so /help#floor-plan gets Floor Plan
  // metadata, /help#emergency gets Emergency Script metadata, etc.
  useEffect(() => {
    const anchor = (location.hash || "").replace("#", "");
    const tool = TOOLS.find((t) => t.id === anchor);
    applyHelpSeo(tool);
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-background bg-texture">
      {/* Top bar (adapts to auth state) */}
      <header className="sticky top-0 z-40 border-b border-border/40 bg-background/80 backdrop-blur">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link
            to={isAuthenticated ? "/dashboard" : "/"}
            className="flex items-center gap-2"
            data-testid="help-logo-link"
          >
            <img src={logoSrc} alt="evntkit" className="h-9 sm:h-10" />
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button
                  variant="outline"
                  className="text-sm gap-2"
                  data-testid="help-back-to-dashboard"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-sm" data-testid="help-signin">
                    Sign in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    className="text-sm bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
                    data-testid="help-signup"
                  >
                    Sign up free
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border/40 bg-card/30">
        <div className="container mx-auto px-4 py-12 sm:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border/60 bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground mb-4">
              <BookOpen className="h-3.5 w-3.5 text-cyan-400" />
              Help Centre
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-4">
              How to use evntkit
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Every tool, what it does, and how to get the most out of it. Jump
              to any tool below — or if you&apos;re new here,{" "}
              {isAuthenticated ? (
                <Link to="/dashboard" className="text-cyan-400 hover:underline">
                  head to your dashboard
                </Link>
              ) : (
                <Link to="/register" className="text-cyan-400 hover:underline">
                  create a free account
                </Link>
              )}{" "}
              and try one.
            </p>
          </div>
        </div>
      </section>

      {/* Content layout: nav + tools */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sticky suite nav */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 space-y-6">
              {SUITES.map((suite) => {
                const suiteTools = TOOLS.filter((t) => t.suite === suite.id);
                return (
                  <SuiteNav key={suite.id} suite={suite} tools={suiteTools} />
                );
              })}
              <div className="pt-4 border-t border-border/40">
                <p className="text-xs text-muted-foreground">
                  Missing something?{" "}
                  {isAuthenticated ? (
                    <Link
                      to="/feedback"
                      className="text-cyan-400 hover:underline"
                    >
                      Send feedback
                    </Link>
                  ) : (
                    <Link to="/register" className="text-cyan-400 hover:underline">
                      Sign up and let us know
                    </Link>
                  )}
                  .
                </p>
              </div>
            </div>
          </aside>

          {/* Tool cards, grouped by suite */}
          <main className="lg:col-span-9 space-y-10">
            {SUITES.map((suite) => {
              const suiteTools = TOOLS.filter((t) => t.suite === suite.id);
              const SuiteIcon = suite.icon;
              return (
                <div key={suite.id} className="space-y-4">
                  <div className="flex items-center gap-3 pb-2 border-b border-border/40">
                    <div
                      className={`p-2 rounded-sm bg-secondary ${suite.color}`}
                    >
                      <SuiteIcon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h2
                        className={`text-2xl uppercase tracking-wider ${suite.color}`}
                      >
                        {suite.label}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {suite.blurb}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {suiteTools.map((tool) => (
                      <ToolCard
                        key={tool.id}
                        tool={tool}
                        isAuthenticated={isAuthenticated}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* General tips card */}
            <Card className="tactical-card border-cyan-500/40" id="tips">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-lg uppercase tracking-wide">
                  <div className="p-2 rounded-sm bg-secondary text-cyan-400">
                    <Sparkles className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  General tips
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground leading-relaxed">
                <ul className="space-y-2 list-disc pl-5">
                  <li>
                    <span className="text-foreground">Metric by default</span>{" "}
                    — flip to Imperial in Settings.
                  </li>
                  <li>
                    <span className="text-foreground">Light & dark themes</span>{" "}
                    — toggle in Settings; branding and colour palette adapt.
                  </li>
                  <li>
                    <span className="text-foreground">Installable PWA</span>{" "}
                    — add evntkit to your phone home screen; works offline for
                    calculators once loaded.
                  </li>
                  <li>
                    <span className="text-foreground">Data privacy</span>{" "}
                    — floor plans, calculator inputs, and tally sessions are
                    stored against your account only.
                  </li>
                  <li>
                    <span className="text-foreground">Delete account any time</span>{" "}
                    — Settings → Account → Delete. Hard delete, no soft-retention.
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* CTA */}
            <div className="text-center pt-6">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
                    data-testid="help-cta-dashboard"
                  >
                    Back to your dashboard
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              ) : (
                <Link to="/register">
                  <Button
                    size="lg"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
                    data-testid="help-cta-signup"
                  >
                    Create your free account
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              )}
            </div>
          </main>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/30">
        <div className="container mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>
            © {new Date().getFullYear()} evntkit • Planning & delivery tools for
            event professionals
          </span>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <Link to="/help" className="hover:text-foreground transition-colors">
              Help
            </Link>
            {!isAuthenticated && (
              <Link
                to="/register"
                className="hover:text-foreground transition-colors"
              >
                Sign up
              </Link>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Help;
